/* =========================================================
   Flexi › Exposure Register — TS models.
   Shapes mirror the backend DTOs under
   Backend/Kuba2.Client.Entities/Flexi/ExposureAssessments/. JSON is PascalCase
   and enums travel as numbers. Instants (CreatedOn, CompletedOn, ModifiedOn)
   are UTC ISO strings; DueDate is a calendar date, "yyyy-MM-dd".
   "DepartmentId" is always the BusinessDepartment id.
   ========================================================= */

export enum ExposureAssessmentStatus {
  InProgress = 0,
  Completed = 1,
}

export enum ExposureTemplateStatus {
  Draft = 0,
  Published = 1,
  Archived = 2,
}

/** Outcome of one factor. `null` on the wire means not assessed yet. */
export enum ExposureHealthCheck {
  Relevant = 1,
  Unclear = 2,
  NotRelevant = 3,
}

/** The four wizard steps, as stored in CurrentStep. */
export enum ExposureAssessmentStep {
  Introduction = 1,
  Departments = 2,
  Assessment = 3,
  Summary = 4,
}

export interface ExposureRegulatoryReference {
  Title: string;
  Url: string | null;
}

// ---------------------------------------------------------------------------
// Business routes — api/kuba/v1/flexi/exposure-assessments
// ---------------------------------------------------------------------------

/** What the caller may do in one business's Exposure Register. */
export interface ExposurePermissions {
  /** Admin or a listed HSE (BHT) advisor of the business; never the business itself. */
  CanAuthor: boolean;
}

export interface ExposureAssessmentSummary {
  Id: number;
  Title: string;
  Status: ExposureAssessmentStatus;
  CurrentStep: ExposureAssessmentStep;
  CreatedOn: string;
  CompletedOn: string | null;
  ResponsibleUserName: string | null;
  /** Already localized by the backend, e.g. "HSE advisor · MDCO". */
  ResponsibleRoleText: string | null;
  IncludedDepartments: number;
  TotalDepartments: number;
  AssessedFactors: number;
  TotalFactors: number;
  RelevantCount: number;
  UnclearCount: number;
  NotRelevantCount: number;
  /** The completion report is archived in Documents. */
  HasReport: boolean;
}

/** The business's "basis for the exposure assessment" texts. */
export interface ExposureProfile {
  IndustryExposures: string | null;
  ProductsAndServices: string | null;
  LocalClarifications: string | null;
  ModifiedOn: string | null;
  ModifiedByUserName: string | null;
}

export interface SaveExposureProfileRequest {
  IndustryExposures: string | null;
  ProductsAndServices: string | null;
  LocalClarifications: string | null;
}

/** A template the business may start an assessment from. */
export interface AssignedExposureTemplate {
  Id: number;
  Name: string;
  Description: string | null;
  Version: number;
  FactorCount: number;
  SectionCount: number;
  IsDefault: boolean;
  LastUsedOn: string | null;
}

export interface StartExposureAssessmentRequest {
  TemplateId: number;
  /** Null lets the backend name it "Exposure assessment {year}". */
  Title: string | null;
}

export interface StartExposureAssessmentResponse {
  Id: number;
}

export interface ExposureAssessmentDepartment {
  /** The BusinessDepartment id. */
  DepartmentId: number;
  Name: string;
  Position: number;
  IsIncluded: boolean;
  ExposureDescription: string | null;
  /** Live while in progress, snapshotted at completion. */
  EmployeeCount: number;
}

export interface ExposureAssessmentFactor {
  Id: number;
  Position: number;
  Title: string;
  /** Paragraphs separated by a blank line. */
  Guidance: string | null;
  RegulatoryReferences: ExposureRegulatoryReference[];
  HealthCheck: ExposureHealthCheck | null;
  Rationale: string | null;
  /** Included departments (BusinessDepartment ids) exposed to the factor. */
  DepartmentIds: number[];
}

export interface ExposureAssessmentSection {
  Name: string;
  Position: number;
  Factors: ExposureAssessmentFactor[];
}

export interface ExposureAssessmentDetail {
  Id: number;
  Title: string;
  Status: ExposureAssessmentStatus;
  CurrentStep: ExposureAssessmentStep;
  TemplateName: string | null;
  ResponsibleUserId: number | null;
  ResponsibleUserName: string | null;
  ResponsibleRoleText: string | null;
  CreatedOn: string;
  CompletedOn: string | null;
  CompletedByUserName: string | null;
  HasReport: boolean;
  IncludedDepartments: number;
  TotalDepartments: number;
  AssessedFactors: number;
  TotalFactors: number;
  RelevantCount: number;
  UnclearCount: number;
  NotRelevantCount: number;
  /** The live profile while in progress, the snapshot once completed. */
  IndustryExposures: string | null;
  ProductsAndServices: string | null;
  LocalClarifications: string | null;
  Departments: ExposureAssessmentDepartment[];
  Sections: ExposureAssessmentSection[];
}

export interface UpdateExposureAssessmentDetailsRequest {
  Title: string;
  ResponsibleUserId: number | null;
  CurrentStep: ExposureAssessmentStep;
}

/** One row of step 2; the request body is a list of these. */
export interface SaveExposureDepartmentRequest {
  DepartmentId: number;
  IsIncluded: boolean;
  ExposureDescription: string | null;
}

export interface AssessExposureFactorRequest {
  HealthCheck: ExposureHealthCheck | null;
  Rationale: string | null;
  DepartmentIds: number[];
}

export interface ExposureActionPointRequest {
  Title: string;
  Description: string | null;
  ResponsibleUserId: number;
  /** "yyyy-MM-dd". */
  DueDate: string;
}

export interface CompleteExposureAssessmentRequest {
  /** Must be true. */
  Confirmed: boolean;
  ActionPoint: ExposureActionPointRequest | null;
}

// ---------------------------------------------------------------------------
// Admin routes — api/kuba/v1/flexi/exposure-templates
// ---------------------------------------------------------------------------

export interface ExposureTemplateSummary {
  Id: number;
  Name: string;
  Description: string | null;
  Status: ExposureTemplateStatus;
  Version: number;
  FactorCount: number;
  SectionCount: number;
  AssignedCount: number;
  IsDefaultForNewBusinesses: boolean;
  CreatedOn: string;
  ModifiedOn: string | null;
}

export interface ExposureTemplateFactor {
  SectionName: string;
  SectionPosition: number;
  Position: number;
  Title: string;
  Guidance: string | null;
  RegulatoryReferences: ExposureRegulatoryReference[];
}

export interface ExposureTemplateDetail {
  Id: number;
  Name: string;
  Description: string | null;
  Status: ExposureTemplateStatus;
  Version: number;
  IsDefaultForNewBusinesses: boolean;
  Factors: ExposureTemplateFactor[];
}

export interface CreateExposureTemplateRequest {
  Name: string;
  Description: string | null;
}

export interface SaveExposureTemplateRequest {
  Name: string;
  Description: string | null;
  Factors: ExposureTemplateFactor[];
}

export interface ExposureTemplateAssignment {
  BusinessId: number;
  BusinessName: string;
  IsDefault: boolean;
  AssignedOn: string;
}

export interface ExposureBusinessCandidate {
  BusinessId: number;
  BusinessName: string;
  HasTemplate: boolean;
}

export interface AssignExposureTemplateRequest {
  BusinessIds: number[];
  MakeDefault: boolean;
}
